import { useAuthStore } from '../stores/authStore';
import { useSiteStore } from '../stores/siteStore';

/**
 * Resolve the active timezone for date/time formatting.
 *
 * Preference order:
 *   1. The currently-selected site's `timezone` (so the alerts feed always
 *      reads in the timezone of the construction site that produced the
 *      event, no matter where the user is browsing from).
 *   2. The user's organization timezone (legacy fallback for views with no
 *      site context, e.g. org-level admin pages).
 *   3. The browser's local timezone (last-resort safety net).
 */
function getActiveTimezone() {
  try {
    const site = useSiteStore.getState().selectedSite;
    const siteTz = site?.timezone;
    if (siteTz) {
      Intl.DateTimeFormat(undefined, { timeZone: siteTz });
      return siteTz;
    }
  } catch {
    // fall through
  }
  try {
    const user = useAuthStore.getState().user;
    const orgTz = user?.organization?.timezone;
    if (orgTz) {
      Intl.DateTimeFormat(undefined, { timeZone: orgTz });
      return orgTz;
    }
  } catch {
    // fall through
  }
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

// Backwards-compat shim — some callers may still import getOrgTimezone.
function getOrgTimezone() { return getActiveTimezone(); }

/**
 * Format a date/datetime string in the active site/org timezone.
 *
 * @param {string|Date} date - ISO date string or Date object
 * @param {object} options - Additional Intl.DateTimeFormat options. Set
 *                            `withAbbr: false` to omit the trailing tz label
 *                            (e.g. " SGT") that's appended by default.
 * @returns {string} Formatted date string with timezone abbreviation
 */
export function formatDateTime(date, options = {}) {
  if (!date) return '';
  const d = new Date(date);
  if (isNaN(d.getTime())) return '';

  const { withAbbr = true, ...fmt } = options;
  const tz = getActiveTimezone();
  const text = d.toLocaleString(undefined, { timeZone: tz, ...fmt });
  return withAbbr ? `${text} ${getTimezoneAbbr(tz)}` : text;
}

/**
 * Format just the date portion in the active site/org timezone.
 */
export function formatDate(date, options = {}) {
  if (!date) return '';
  const d = new Date(date);
  if (isNaN(d.getTime())) return '';

  // Plain dates never get a tz suffix — it would be noisy and misleading
  // since a date has no time-of-day to interpret in another zone.
  const { withAbbr: _ignored, ...fmt } = options;
  const tz = getActiveTimezone();
  return d.toLocaleDateString(undefined, { timeZone: tz, ...fmt });
}

/**
 * Format just the time portion in the active site/org timezone.
 */
export function formatTime(date, options = {}) {
  if (!date) return '';
  const d = new Date(date);
  if (isNaN(d.getTime())) return '';

  const { withAbbr = true, ...fmt } = options;
  const tz = getActiveTimezone();
  const text = d.toLocaleTimeString(undefined, { timeZone: tz, ...fmt });
  return withAbbr ? `${text} ${getTimezoneAbbr(tz)}` : text;
}

/**
 * Public accessor for the active timezone (IANA, e.g. "Asia/Singapore") and
 * its short abbreviation (e.g. "SGT"). Use this in headers/banners that want
 * to tell the user which clock the displayed times are on.
 */
export function getActiveTimezoneInfo() {
  const tz = getActiveTimezone();
  return { timezone: tz, abbr: getTimezoneAbbr(tz) };
}

/** "YYYY-MM-DD" for an instant as seen in the active (site) timezone. */
export function ymdInActiveTimezone(date = new Date()) {
  const tz = getActiveTimezone();
  try {
    // en-CA renders ISO-style YYYY-MM-DD.
    return new Intl.DateTimeFormat('en-CA', {
      timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit',
    }).format(date);
  } catch {
    return new Intl.DateTimeFormat('en-CA', {
      year: 'numeric', month: '2-digit', day: '2-digit',
    }).format(date);
  }
}

/**
 * Default alerts date range: 1st of the current month → today, evaluated in the
 * site's timezone. So on June 19 (SGT) it returns 2026-06-01 → 2026-06-19, and
 * on the 20th it returns 2026-06-01 → 2026-06-20. Used to seed the filter on load.
 */
export function currentMonthToDateRange() {
  const today = ymdInActiveTimezone();          // e.g. "2026-06-19"
  const startDate = `${today.slice(0, 7)}-01`;  // "2026-06-01"
  return { startDate, endDate: today };
}

/**
 * Curated IANA → short-abbr lookup for the timezones we actually serve. The
 * browser's Intl.DateTimeFormat returns generic 'GMT+8' style strings for
 * most en-US locales, which isn't useful for users who think in 'SGT' /
 * 'IST'. Keep this list focused on what KenSafety customers operate in.
 */
const IANA_TO_ABBR = {
  'Asia/Singapore':   'SGT',
  'Asia/Kolkata':     'IST',
  'Asia/Calcutta':    'IST',
  'Asia/Kuala_Lumpur':'MYT',
  'Asia/Jakarta':     'WIB',
  'Asia/Bangkok':     'ICT',
  'Asia/Manila':      'PHT',
  'Asia/Tokyo':       'JST',
  'Asia/Seoul':       'KST',
  'Asia/Shanghai':    'CST',
  'Asia/Dubai':       'GST',
  'Australia/Sydney': 'AEDT',
  'Europe/London':    'GMT',
  'America/Toronto':  'EST',
  'America/New_York': 'EST',
  'America/Los_Angeles': 'PST',
  'UTC':              'UTC',
};

/**
 * Get the short timezone abbreviation (e.g., "SGT", "IST") for a given IANA timezone.
 * Falls back to whatever Intl returns (typically a GMT±N offset), then to
 * the IANA string itself.
 */
function getTimezoneAbbr(tz) {
  if (IANA_TO_ABBR[tz]) return IANA_TO_ABBR[tz];
  try {
    const parts = new Intl.DateTimeFormat('en-US', { timeZone: tz, timeZoneName: 'short' }).formatToParts(new Date());
    const tzPart = parts.find(p => p.type === 'timeZoneName');
    return tzPart?.value || tz;
  } catch {
    return tz;
  }
}

/**
 * Get dual-time display info: site time + user's local time.
 * Returns { siteTime, siteLabel, localTime, localLabel, isSameZone }.
 */
export function getDualTimeInfo(date) {
  const d = date ? new Date(date) : new Date();
  if (isNaN(d.getTime())) return null;

  const siteTimezone = getOrgTimezone();
  const localTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const isSameZone = siteTimezone === localTimezone;

  const timeFormat = { hour: '2-digit', minute: '2-digit', hour12: true };

  const siteTime = d.toLocaleTimeString('en-US', { timeZone: siteTimezone, ...timeFormat });
  const siteLabel = getTimezoneAbbr(siteTimezone);

  const localTime = d.toLocaleTimeString('en-US', { timeZone: localTimezone, ...timeFormat });
  const localLabel = getTimezoneAbbr(localTimezone);

  return { siteTime, siteLabel, localTime, localLabel, isSameZone };
}
